import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import {
    FormGroup,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import { CodelistService } from '@common/services/codelist.service';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { NominatedAgentService } from '@services/nominated-agent.service';
import { ToastService } from '@services/toast.service';
import { EditVehicleModalModel } from 'app/tos-operations/models/EditVehicleModalModel';
import { Observable, of, Subject } from 'rxjs';

@Component({
    selector: 'app-manifest-vehicle-edit-modal',
    templateUrl: './manifest-vehicle-edit-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManifestVehicleEditModalComponent implements OnInit, OnDestroy {
    public title: string;
    public message: string;
    public id?: number;
    public cargoManifestId?: any;

    model: EditVehicleModalModel = new EditVehicleModalModel();
    form: UntypedFormGroup;
    isBusy: boolean = false;
    showIMDG: boolean = false;

    adrs$: Observable<any> = of([]);
    linkedUnitOptions$: Observable<any> = of([]);
    unNumberValue: string = null;
    imdgValue: string = null;
    private preserveExistingLinkedUnitWithNestedTrailers = false;
    private linkedUnitOptionsCurrentVehicleId: number = null;

    eitherVINOrLicencePlateRef = this.eitherVINOrLicencePlateValidator();

    destroyed$ = new Subject<void>();

    constructor(
        public activeModal: DialogRef,
        public nominatedAgentService: NominatedAgentService,
        private codeListService: CodelistService,
        private toastyService: ToastService
    ) {}

    ngOnInit(): void {
        this.adrs$ = this.codeListService.getAdrByCriteria();

        this.form = this.model.createFormGroup();
        this.form.setValidators(this.eitherVINOrLicencePlateRef);

        if (this.id) {
            this.populateDataForEdit();
        } else {
            this.populateDataForCreate();
            this.setupValueChangeListeners();
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    populateDataForCreate() {
        this.model = new EditVehicleModalModel();
        this.preserveExistingLinkedUnitWithNestedTrailers = false;
        this.linkedUnitOptionsCurrentVehicleId = null;
        this.loadLinkedUnitOptions();
        this.setWeightControls(false);
    }

    populateDataForEdit() {
        this.isBusy = true;
        this.nominatedAgentService.getEditDataForManifestVehicle(this.id).subscribe((data) => {
            if (data) {
                this.model = new EditVehicleModalModel(data);

                if (data.trailers?.length > 0) {
                    const trailersArray = this.form.get('trailers') as UntypedFormArray;
                    trailersArray.clear();
                    data.trailers.forEach((trailer) => {
                        const trailerGroup = this.createTrailerGroup();
                        trailerGroup.patchValue(trailer);
                        trailersArray.push(trailerGroup);
                    });
                }

                this.setWeightControls(data.hasCargo);

                if (data.hasDangerousCargo) {
                    this.showIMDG = true;
                }

                this.setupValueChangeListeners();

                this.preserveExistingLinkedUnitWithNestedTrailers = data.linkedUnitVehicleId != null;
                this.linkedUnitOptionsCurrentVehicleId = data.id ?? this.id ?? null;
                this.form.reset(data);
                this.clearLinkedUnitIfNestedTrailerEditorActive();
                this.loadLinkedUnitOptions();

                this.unNumberValue = this.form.get('unNumber').value;
                this.imdgValue = this.form.get('imdgPackingGroup').value;

                this.isBusy = false;
            }
        });
    }

    addTrailer() {
        const trailersArray = this.form.get('trailers') as UntypedFormArray;
        const newTrailerGroup = this.createTrailerGroup();

        newTrailerGroup.get('trailerHasCargo').valueChanges.subscribe((hasCargo) => {
            const cargoGrossWeightControl = newTrailerGroup.get('trailerCargoGrossWeight');
            const cargoNetWeightControl = newTrailerGroup.get('trailerCargoNetWeight');
            const particularsControl = newTrailerGroup.get('trailerParticulars');
            const imdgControl = newTrailerGroup.get('trailerHasDangerousCargo');

            if (hasCargo) {
                cargoGrossWeightControl.enable();
                cargoNetWeightControl.enable();
                particularsControl.enable();
                imdgControl.enable();
            } else {
                cargoGrossWeightControl.disable();
                cargoNetWeightControl.disable();
                particularsControl.disable();
                imdgControl.setValue(false);
                imdgControl.disable();
            }

            cargoGrossWeightControl.updateValueAndValidity();
            cargoNetWeightControl.updateValueAndValidity();
            particularsControl.updateValueAndValidity();
            particularsControl.updateValueAndValidity();
        });

        newTrailerGroup.get('trailerLength').valueChanges.subscribe((value) => {
            const compositionLengthControl = this.form.get('compositionLength');
            let compositionLength = Number(this.form.get('length').value) || 0;
            trailersArray.controls.forEach((trailer) => {
                compositionLength += Number(trailer.get('trailerLength').value) || 0;
            });

            compositionLengthControl.setValue(compositionLength, { emitEvent: false });
        });

        trailersArray.push(newTrailerGroup);
        this.clearLinkedUnitIfNestedTrailerEditorActive();
    }

    removeTrailer(i: number) {
        const trailersArray = this.form.get('trailers') as UntypedFormArray;
        trailersArray.at(i).get('trailerLength').setValue(null);
        trailersArray.removeAt(i);
    }

    createTrailerGroup(): UntypedFormGroup {
        return new UntypedFormGroup({
            cargoManifestItemVehicleId: new UntypedFormControl({ value: null, disabled: false }),
            trailerLicencePlate: new UntypedFormControl({ value: null, disabled: false }),
            trailerLength: new UntypedFormControl({ value: null, disabled: false }),
            trailerTaraWeight: new UntypedFormControl({ value: null, disabled: false }),
            trailerHasCargo: new UntypedFormControl({ value: null, disabled: false }),
            trailerCargoNetWeight: new UntypedFormControl({ value: null, disabled: true }),
            trailerCargoGrossWeight: new UntypedFormControl({ value: null, disabled: false }),
            trailerParticulars: new UntypedFormControl({ value: null, disabled: false }),
            trailerHasDangerousCargo: new UntypedFormControl({ value: null, disabled: false }),
            trailerIMDG: new UntypedFormControl({ value: null, disabled: false }),
            trailerUNNumber: new UntypedFormControl({ value: null, disabled: false }),
            trailerImdgPackaginGroup: new UntypedFormControl({ value: null, disabled: false }),
            trailerTechnicalName: new UntypedFormControl({ value: null, disabled: false }),
            trailerDGContact: new UntypedFormControl({ value: null, disabled: false }),
            trailerContainerNumberFirst: new UntypedFormControl({ value: null, disabled: false }),
            trailerContainerNumberSecond: new UntypedFormControl({ value: null, disabled: false }),
            newVehicle: new UntypedFormControl({ value: null, disabled: false })
        });
    }

    save() {
        const data = this.form.getRawValue();
        data.cargoManifestId = this.cargoManifestId;
        data.id = this.id;
        if (!this.showLinkedUnitField()) {
            data.linkedUnitVehicleId = null;
        }

        this.nominatedAgentService.saveCargoManifestItemVehicle(data).subscribe({
            next: () => {
                this.activeModal.close(true);
            },
            error: (error) => {
                this.toastyService.error(error?.error?.errorMessage);
                this.activeModal.close();
            }
        });
    }

    hasCargo() {
        return this.form?.get('hasCargo')?.value ?? false;
    }

    toggleShowIMDG() {
        if (this.hasCargo()) {
            this.showIMDG = !this.showIMDG;
        }
    }

    eitherVINOrLicencePlateValidator(): ValidatorFn {
        return (group: FormGroup): ValidationErrors | null => {
            const vin = group.controls['vehicleNumber'].value;
            const licencePlate = group.controls['licencePlate'].value;

            if ((vin && !licencePlate) || (!vin && licencePlate) || (vin && licencePlate)) {
                return null;
            } else {
                return { eitherVINOrLicencePlate: true };
            }
        };
    }

    private setupValueChangeListeners() {
        this.trackHasCargoChanges();
        this.trackLengthChanges();
        this.trackLinkedUnitVisibilityChanges();
    }

    private trackHasCargoChanges() {
        this.form.get('hasCargo').valueChanges.subscribe((hasCargo) => {
            this.setWeightControls(hasCargo);
        });
    }

    private setWeightControls(hasCargo: any) {
        const cargoGrossWeightControl = this.form.get('cargoGrossWeight');
        const particularsControl = this.form.get('particulars');
        const cargoNetWeightControl = this.form.get('cargoNetWeight');
        const dangerousCargoControl = this.form.get('hasDangerousCargo');

        if (hasCargo) {
            cargoGrossWeightControl.enable();
            particularsControl.enable();
            cargoNetWeightControl.enable();
            dangerousCargoControl.enable();
        } else {
            cargoGrossWeightControl.disable();
            particularsControl.disable();
            cargoNetWeightControl.disable();
            dangerousCargoControl.setValue(false);
            dangerousCargoControl.disable();
            this.showIMDG = false;
        }

        cargoGrossWeightControl.updateValueAndValidity();
        cargoNetWeightControl.updateValueAndValidity();
        particularsControl.updateValueAndValidity();
    }

    private trackLengthChanges() {
        this.form.get('length').valueChanges.subscribe((length) => {
            const compositionLengthControl = this.form.get('compositionLength');
            if (length) {
                let compositionLength = Number(length) || 0;
                const trailers = this.form.get('trailers') as UntypedFormArray;
                trailers.controls.forEach((trailer) => {
                    compositionLength += Number(trailer.get('trailerLength').value) || 0;
                });
                compositionLengthControl.setValue(compositionLength, { emitEvent: false });
            } else {
                compositionLengthControl.setValue(0);
            }
        });
    }

    onUnNumberChange(value: string) {
        if (value === this.unNumberValue) return;

        this.unNumberValue = value;

        if (!this.unNumberValue) {
            this.adrs$ = this.codeListService.getAdrByCriteria();

            return;
        }

        const imdgValue = this.imdgValue ?? null;
        this.adrs$ = this.codeListService.getAdrByCriteria(value, imdgValue);
    }

    onImdgPackingGroupChange(value: string) {
        if (value === this.imdgValue) return;

        this.imdgValue = value;

        if (!this.unNumberValue) {
            this.adrs$ = this.codeListService.getAdrByCriteria();

            return;
        }

        this.adrs$ = this.codeListService.getAdrByCriteria(this.unNumberValue, value);
    }

    showLinkedUnitField(): boolean {
        if (!this.form) return true;

        const trailers = this.form.get('trailers') as UntypedFormArray;
        return (
            this.form.get('vehicleTypeId')?.value !== 'TRK' ||
            trailers.length === 0 ||
            this.preserveExistingLinkedUnitWithNestedTrailers
        );
    }

    private loadLinkedUnitOptions() {
        if (!this.cargoManifestId) {
            this.linkedUnitOptions$ = of([]);
            return;
        }

        const filter = {
            cargoManifestId: this.cargoManifestId,
            currentVehicleId: this.linkedUnitOptionsCurrentVehicleId ?? this.id ?? null
        };

        this.linkedUnitOptions$ = this.nominatedAgentService.getManifestVehicleLinkedUnitOptions(filter);
    }

    private trackLinkedUnitVisibilityChanges() {
        this.form.get('vehicleTypeId').valueChanges.subscribe(() => {
            this.clearLinkedUnitIfNestedTrailerEditorActive();
        });
    }

    private clearLinkedUnitIfNestedTrailerEditorActive() {
        if (!this.showLinkedUnitField()) {
            this.form.get('linkedUnitVehicleId')?.setValue(null);
        }
    }
}
